import "@/styles/index.scss";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./i18n/config.ts";
import styles from "@/styles/App.module.scss";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { StatusError } from "./api/client.ts";
import ErrorBoundary from "./components/app/ErrorBoundaryOutsideAuth.tsx";
import IntercomProvider from "./providers/Intercom.tsx";
import RouterSetup from "./components/RouterSetup.tsx";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A single transient failure used to be permanent: with retry disabled,
      // a query that never resolves leaves gated screens (see
      // BuildingUserProvider) loading forever. Only server-side and network
      // failures are worth retrying — a 4xx will answer the same way three
      // times, and retrying it just delays the UI that handles it.
      retry: (failureCount, error) => {
        // Raw axios errors carry the status on `response`; ones wrapped by
        // throwCaughtError carry it on `status`.
        const status =
          (error as AxiosError)?.response?.status ??
          (error as StatusError)?.status;
        // Timeouts and rate limits are transient despite being 4xx.
        const transient = status === 408 || status === 429;
        if (status && status >= 400 && status < 500 && !transient) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => 1000 * 2 ** attempt,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary className={styles.topLevelErrorBoundary}>
      <QueryClientProvider client={queryClient}>
        <IntercomProvider>
          <RouterSetup />
        </IntercomProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
